// Pure decision core for live customer refresh (M7 Chunk 3 / WS5 / ASK-080 §E).
//
// When a merchant publishes, a new bake lands → the customer's boot.json librarySha
// changes. The customer runtime should hot-swap the scene WITHOUT yanking the user:
// swap at a safe idle boundary, else (if they never idle) surface a gentle nudge.
// Ratified (Shaun 2026-06-23): auto-swap on idle (no open product/hero/checkout/drag
// + a short input lull) + a staleness-cap nudge if pending too long while busy.
//
// A pending bake is LOOK/LAYOUT/FIXTURES only — price/availability live-fetch per
// DECISION-043 — so there's no urgency to force a swap mid-interaction.
//
// Kept dependency-free (no DOM/THREE) so it's unit-testable; the app wiring tracks
// the shas/timestamps + interaction flags and calls evaluateRefresh() each tick, then
// performs the actual fetch→setFullSceneContext swap + state capture/restore.

export const LIVE_REFRESH_DEFAULTS = Object.freeze({
	lullMs: 1500,      // input must be quiet this long after the last gesture before we swap
	staleCapMs: 75000, // pending-but-never-idle this long → show the manual nudge instead
});

/**
 * @typedef {Object} RefreshCtx
 * @property {string|null} loadedSha     librarySha the customer's scene was built from
 * @property {string|null} latestSha     librarySha from the freshly-polled boot
 * @property {boolean} interacting       open product/hero/checkout/cart/active-drag right now
 * @property {number} nowMs
 * @property {number} lastInputMs        timestamp of the user's last input (for the lull)
 * @property {number|null} pendingSinceMs  when the change was first detected (null if not pending)
 * @property {number=} lullMs
 * @property {number=} staleCapMs
 */

/**
 * Decide what the live-refresh loop should do this tick. Pure.
 * @param {RefreshCtx} ctx
 * @returns {{ state:("current"|"pending"|"swap"|"nudge"), reason:string }}
 */
export function evaluateRefresh(ctx) {
	const { loadedSha, latestSha, interacting, nowMs, lastInputMs, pendingSinceMs } = ctx || {};
	const lullMs = Number.isFinite(ctx?.lullMs) ? ctx.lullMs : LIVE_REFRESH_DEFAULTS.lullMs;
	const staleCapMs = Number.isFinite(ctx?.staleCapMs) ? ctx.staleCapMs : LIVE_REFRESH_DEFAULTS.staleCapMs;

	// No newer bake (or unknown) → nothing pending.
	if (!latestSha || latestSha === loadedSha) return { state: "current", reason: "sha unchanged" };

	// A newer bake exists → pending. Decide swap vs nudge vs wait.
	const idle = !interacting && Number.isFinite(lastInputMs) && (nowMs - lastInputMs >= lullMs);
	if (idle) return { state: "swap", reason: "idle boundary reached" };

	if (Number.isFinite(pendingSinceMs) && (nowMs - pendingSinceMs >= staleCapMs)) {
		return { state: "nudge", reason: "pending past staleness cap while busy" };
	}
	return { state: "pending", reason: interacting ? "user interacting" : "awaiting input lull" };
}

/**
 * Capture the customer-view state that must survive a hot-swap (camera + cart +
 * the open product, so it can be re-opened iff it still exists in the new bake).
 * Pure: the app passes plain values; restore is the inverse the app applies.
 */
export function captureRefreshState({ camera, cart, openProductId } = {}) {
	return {
		camera: camera ? { position: { ...camera.position }, target: camera.target ? { ...camera.target } : null } : null,
		cart: cart || null,                 // app.state.cart is already plain
		openProductId: openProductId || null,
	};
}

/**
 * After a swap, decide whether the previously-open product can be re-opened — only
 * if it still exists in the new bake's product set. Pure.
 * @param {string|null} openProductId
 * @param {Set<string>|string[]} newProductIds
 * @returns {boolean}
 */
export function canReopenProduct(openProductId, newProductIds) {
	if (!openProductId) return false;
	const set = newProductIds instanceof Set ? newProductIds : new Set(newProductIds || []);
	return set.has(openProductId);
}
